import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Crown, Loader2, CheckCircle2, Copy, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Pill } from "./ui";

const inputCls = "w-full border border-[#D9E2EF] rounded-sm px-3 py-2.5 text-sm text-[#0B1B33] bg-white focus:outline-none focus:border-[#2563EB] transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-[#7C8AA3] mb-1.5";
const btnPrimary = "inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold tracking-wide px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-2 border border-[#D9E2EF] text-[#43546D] hover:border-[#2563EB] hover:text-[#2563EB] text-xs font-semibold tracking-wide px-5 py-2.5 rounded-sm transition-colors";

export default function AdminCreateMembership() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", password: "", notes: "" });
  const [plan, setPlan] = useState("");
  const [plans, setPlans] = useState([]);
  const [accessType, setAccessType] = useState("PAID_MEMBERSHIP");
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/membership/plans").then((r) => setPlans(r.data || [])).catch(() => {}); }, []);

  const submit = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/admin/members/create", {
        name: form.name, email: form.email, company: form.company, phone: form.phone,
        plan, password: form.password, access_type: accessType, notes: form.notes,
      });
      setResult(data);
      toast.success("Membership created");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const reset = () => {
    setResult(null); setPlan(""); setAccessType("PAID_MEMBERSHIP");
    setForm({ name: "", email: "", company: "", phone: "", password: "", notes: "" });
  };

  const canSubmit = form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email) && !!plan;

  return (
    <div data-testid="page-create-membership">
      <PageHeader kicker="Membership" title="CREATE MEMBERSHIP"
        sub="Admin-created member account — choose a plan and access type. Credentials can be auto-generated and shared with the member.">
        <Link to="/admin/members" data-testid="cm-view-members-btn" className={btnGhost}>ACTIVE MEMBERS <ArrowRight size={13} /></Link>
      </PageHeader>

      {!result ? (
        <div className="adm-card p-6 md:p-8 max-w-3xl" data-testid="cm-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Full Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cm-name-input" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cm-email-input" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Company</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="cm-company-input" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cm-phone-input" className={inputCls} placeholder="+91 …" />
            </div>
            <div>
              <label className={labelCls}>Membership Plan *</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="cm-plan-select" className={inputCls}>
                <option value="">Select plan…</option>
                {plans.map((p) => <option key={p.name} value={p.name}>{`${p.name} — ₹${String(p.price).replace(/^₹\s*/, "")}`}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Password (optional)</label>
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cm-password-input" className={inputCls} placeholder="Leave blank to auto-generate" />
            </div>
          </div>

          <p className={`${labelCls} mt-6`}>Access Type *</p>
          <div className="grid md:grid-cols-2 gap-4">
            <button onClick={() => setAccessType("PAID_MEMBERSHIP")} data-testid="cm-access-paid"
              className={`text-left border rounded-sm p-4 transition-colors ${accessType === "PAID_MEMBERSHIP" ? "border-[#2563EB] ring-1 ring-[#2563EB]/30 bg-[#F5F8FF]" : "border-[#E4EAF3] hover:border-[#2563EB]/50"}`}>
              <Crown size={18} className="text-[#2563EB]" />
              <p className="mt-2 font-bold text-[#0B1B33] text-[13px] tracking-wide">PAID MEMBERSHIP</p>
              <p className="mt-1 text-[11.5px] text-[#7C8AA3]">Payment collected via membership checkout — marked PAYMENT PENDING until paid.</p>
            </button>
            <button onClick={() => setAccessType("ADMIN_APPROVED_FREE")} data-testid="cm-access-free"
              className={`text-left border rounded-sm p-4 transition-colors ${accessType === "ADMIN_APPROVED_FREE" ? "border-emerald-500 ring-1 ring-emerald-500/30 bg-emerald-50/50" : "border-[#E4EAF3] hover:border-emerald-400"}`}>
              <UserPlus size={18} className="text-emerald-600" />
              <p className="mt-2 font-bold text-[#0B1B33] text-[13px] tracking-wide">ADMIN APPROVED (FREE ACCESS)</p>
              <p className="mt-1 text-[11.5px] text-[#7C8AA3]">No payment required — membership active immediately.</p>
            </button>
          </div>

          <div className="mt-5">
            <label className={labelCls}>Internal Notes (optional)</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cm-notes-input" className={`${inputCls} resize-none`} />
          </div>

          <button onClick={submit} disabled={working || !canSubmit} data-testid="cm-submit-btn" className={`${btnPrimary} mt-6`}>
            {working ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} CREATE MEMBERSHIP
          </button>
        </div>
      ) : (
        <div className="adm-card p-10 text-center max-w-2xl" data-testid="cm-success-panel">
          <CheckCircle2 size={42} className="text-emerald-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0B1B33]">MEMBERSHIP CREATED</h2>
          <p className="mt-1.5 text-[13px] text-[#5B6B87]">{result.name} · {result.email}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2.5">
            <Pill value="ACTIVE MEMBER" />
            <span className="adm-pill text-slate-600 border-slate-300 bg-slate-50" data-testid="cm-result-plan">{result.plan}</span>
            <span className="adm-pill text-slate-600 border-slate-300 bg-slate-50" data-testid="cm-result-access">{result.access_type.replace(/_/g, " ")}</span>
            <span className="adm-pill text-slate-600 border-slate-300 bg-slate-50" data-testid="cm-result-payment">PAYMENT {result.payment_status}</span>
          </div>
          {result.generated_password && (
            <div className="mt-6 inline-flex items-center gap-3 border border-amber-300 bg-amber-50 rounded-sm px-5 py-3" data-testid="cm-generated-password">
              <span className="font-mono text-sm font-bold text-[#0B1B33]">{result.generated_password}</span>
              <button onClick={() => { navigator.clipboard.writeText(result.generated_password); toast.success("Password copied"); }}
                data-testid="cm-copy-password-btn" className="text-[#2563EB] hover:text-[#1D4ED8]"><Copy size={15} /></button>
            </div>
          )}
          {result.generated_password && <p className="mt-2 text-[11px] text-amber-700">Auto-generated password — share it with the member now. It is shown only once.</p>}
          <div className="mt-7 flex justify-center gap-3">
            <button onClick={reset} data-testid="cm-create-another-btn" className={btnGhost}>CREATE ANOTHER</button>
            <Link to="/admin/members" data-testid="cm-goto-members-btn" className={btnPrimary}>VIEW ACTIVE MEMBERS <ArrowRight size={13} /></Link>
          </div>
        </div>
      )}
    </div>
  );
}
